import { useEffect, useRef, useState } from 'react'
import ApexCharts from 'apexcharts'

export type ViewportInfo = {
  width: number
  height: number
  isMobile: boolean
  isTablet: boolean
  isLandscape: boolean
}

function readViewport(): ViewportInfo {
  if (typeof window === 'undefined') {
    return { width: 1200, height: 800, isMobile: false, isTablet: false, isLandscape: false }
  }
  const w = window.innerWidth
  const h = window.innerHeight
  return {
    width: w,
    height: h,
    isMobile: w < 576,
    isTablet: w >= 576 && w < 992,
    isLandscape: w > h,
  }
}

export function useViewport(): ViewportInfo {
  const [viewport, setViewport] = useState<ViewportInfo>(readViewport)

  useEffect(() => {
    const update = () => setViewport(readViewport())
    window.addEventListener('resize', update)
    window.addEventListener('orientationchange', update)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
    }
  }, [])

  return viewport
}

export function resolveLoraChartHeight(viewport: ViewportInfo, isFullscreen: boolean): number {
  if (isFullscreen) {
    return Math.max(280, viewport.height - (viewport.isMobile ? 72 : 96))
  }
  if (viewport.isMobile) {
    if (viewport.isLandscape) {
      return Math.min(Math.max(viewport.height - 140, 260), 420)
    }
    return Math.min(Math.max(viewport.height * 0.42, 300), 380)
  }
  if (viewport.isTablet) {
    return viewport.isLandscape ? 400 : 380
  }
  return 480
}

export interface TouchHandlersProps {
  chartId: string
  xMin: number
  xMax: number
  isMobile: boolean
  containerRef: React.RefObject<HTMLDivElement>
  onZoomed?: (min: number, max: number) => void
  isCategoryChart?: boolean
}

export function useChartTouchHandlers({
  chartId,
  xMin,
  xMax,
  isMobile,
  containerRef,
  onZoomed,
  isCategoryChart = false,
}: TouchHandlersProps) {
  const activeTouchRef = useRef<{
    initialMin: number
    initialMax: number
    initialDist: number
    initialCenterX: number
    startX: number
    startY: number
    isPanning: boolean
    isZooming: boolean
    isSwipeDirectionDetermined: boolean
  } | null>(null)

  useEffect(() => {
    if (!isMobile) return
    const container = containerRef.current
    if (!container) return

    const getChartRange = () => {
      const chartInstance = ApexCharts.getChartByID(chartId) as any
      if (chartInstance && chartInstance.w && chartInstance.w.globals) {
        const min = chartInstance.w.globals.minX
        const max = chartInstance.w.globals.maxX
        if (typeof min === 'number' && typeof max === 'number' && !isNaN(min) && !isNaN(max)) {
          return { min, max }
        }
      }
      return { min: xMin, max: xMax }
    }

    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement
      if (target.closest('.apexcharts-toolbar') || target.closest('.btn')) {
        return // Avoid overriding buttons or toolbar interactions
      }

      const { min, max } = getChartRange()

      if (e.touches.length === 2) {
        // Pinch-to-zoom init
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]
        const dist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY)
        const rect = container.getBoundingClientRect()
        const centerX = (touch1.clientX + touch2.clientX) / 2 - rect.left

        activeTouchRef.current = {
          initialMin: min,
          initialMax: max,
          initialDist: dist,
          initialCenterX: centerX,
          startX: 0,
          startY: 0,
          isPanning: false,
          isZooming: true,
          isSwipeDirectionDetermined: true,
        }
      } else if (e.touches.length === 1) {
        // Drag-to-pan init
        const touch = e.touches[0]
        activeTouchRef.current = {
          initialMin: min,
          initialMax: max,
          initialDist: 0,
          initialCenterX: 0,
          startX: touch.clientX,
          startY: touch.clientY,
          isPanning: false,
          isZooming: false,
          isSwipeDirectionDetermined: false,
        }
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      const state = activeTouchRef.current
      if (!state) return

      if (e.touches.length === 2 && state.isZooming && state.initialDist > 0) {
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]
        const dist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY)

        if (dist > 5) {
          const zoomFactor = state.initialDist / dist
          const currentWidth = state.initialMax - state.initialMin
          const newWidth = currentWidth * zoomFactor

          const rect = container.getBoundingClientRect()
          const ratio = state.initialCenterX / rect.width

          let newMin = state.initialMin + (currentWidth - newWidth) * ratio
          let newMax = newMin + newWidth

          // Boundary limiters
          if (newMin < xMin) newMin = xMin
          if (newMax > xMax) newMax = xMax
          if (newMin < xMin) newMin = xMin
          if (newMax > xMax) newMax = xMax

          const minAllowedRange = isCategoryChart ? 2 : 5 * 60 * 1000 // min 2 categories or 5 mins
          if (newMax - newMin >= minAllowedRange) {
            ApexCharts.exec(chartId, 'zoomX', newMin, newMax)
            onZoomed?.(newMin, newMax)
          }
          e.preventDefault()
        }
      } else if (e.touches.length === 1 && !state.isZooming) {
        const touch = e.touches[0]
        const dx = touch.clientX - state.startX
        const dy = touch.clientY - state.startY

        if (!state.isSwipeDirectionDetermined) {
          if (Math.hypot(dx, dy) > 10) {
            state.isSwipeDirectionDetermined = true
            if (Math.abs(dx) > Math.abs(dy)) {
              state.isPanning = true
            }
          }
        }

        if (state.isPanning) {
          e.preventDefault()
          const rect = container.getBoundingClientRect()
          const containerWidth = rect.width

          const currentWidth = state.initialMax - state.initialMin
          const shiftTime = (-dx / containerWidth) * currentWidth

          let newMin = state.initialMin + shiftTime
          let newMax = state.initialMax + shiftTime

          if (newMin < xMin) {
            const diff = xMin - newMin
            newMin += diff
            newMax += diff
          }
          if (newMax > xMax) {
            const diff = newMax - xMax
            newMin -= diff
            newMax -= diff
          }

          if (newMin < xMin) newMin = xMin
          if (newMax > xMax) newMax = xMax

          ApexCharts.exec(chartId, 'zoomX', newMin, newMax)
          onZoomed?.(newMin, newMax)
        }
      }
    }

    const handleTouchEnd = () => {
      activeTouchRef.current = null
    }

    container.addEventListener('touchstart', handleTouchStart, { passive: false })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd, { passive: true })
    container.addEventListener('touchcancel', handleTouchEnd, { passive: true })

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
      container.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [chartId, xMin, xMax, isMobile, containerRef, onZoomed, isCategoryChart])
}
